import { useEffect, useState } from 'react';

import {
  Anchor,
  Box,
  FormHelp,
  Heading,
  IconButton,
  Modal,
  Paragraph,
  Text,
  TextButton,
} from '@zillow/constellation-10';
import {
  IconChevronLeftCircleFilled,
  IconChevronRightCircleFilled,
  IconDeleteOutline,
} from '@zillow/constellation-icons';

import { TextareaHelper } from './TextareaHelper';

const RIGHT = '-1';
const LEFT = '+1';

export type PhotoProps = {
  caption: string;
  id: string;
  rank: number | string;
  url: string;
  error?: string;
  feedId?: string;
  feedListingId?: string;
  isAppropriate?: boolean;
  thumbUrl?: string;
  errorDto?: {
    errorCode?: string;
    errorMessage?: string;
    replacementPhotoId?: string;
  };
};

const getUrlSrc = (
  photo: PhotoProps,
  getPhotoUrls: (
    isRunningInProd: boolean,
    photoKey: string,
  ) => {
    medium: string;
    thumb: string;
  },
  isRunningInProd: boolean,
) => {
  if (photo?.errorDto?.errorCode) {
    if (photo?.errorDto?.replacementPhotoId) {
      return getPhotoUrls(isRunningInProd, photo?.errorDto?.replacementPhotoId).medium;
    }

    return '';
  }

  return photo.url ? photo.url.replace('http://photo', 'https://photonet') : '';
};

type Props = {
  onClose: () => void;
  idx: number;
  additionalDisabledChecks?: boolean;
  /**
   * disable the caption feature
   *
   * captions are not supported on zillow
   * support date is unknown
   */
  isCaptionDisabled?: boolean;
  photos: Array<PhotoProps>;
  onDeletePhoto: (args: { photoIdAtSlidePosition: string; onComplete: () => void; slidePosition: number }) => void;
  onCaptionUpdate?: (args: { photoId: string; caption: string; onComplete: () => void; slidePosition: number }) => void;
  onClearError: (args: { photoAtSlidePosition: PhotoProps; slidePosition: number }) => void;
  getPhotoUrls?: (
    isRunningInProd: boolean,
    photoKey: string,
  ) => {
    medium: string;
    thumb: string;
  };
  isRunningInProd?: boolean;
};

export const PhotoDetailsModal = ({
  additionalDisabledChecks,
  idx,
  isCaptionDisabled = false,
  onCaptionUpdate,
  onClearError,
  onClose,
  onDeletePhoto,
  photos,
  getPhotoUrls = () => ({ medium: '', thumb: '' }),
  isRunningInProd = false,
}: Props) => {
  const [isCaptionSaving, setIsCaptionSaving] = useState(false);
  const [isPhotoDeleting, setIsPhotoDeleting] = useState(false);
  const [slidePosition, setSlidePosition] = useState(idx);

  const isActionButtonDisabled = additionalDisabledChecks || isCaptionSaving || isPhotoDeleting;

  useEffect(
    () =>
      photos.forEach((image: PhotoProps) => {
        new Image().src = image.url;
      }),
    [],
  );

  const getNextSlideIdx = (direction: string) => {
    let nextSlideIdx;
    if (direction === LEFT) {
      nextSlideIdx = slidePosition - 1 < 0 ? photos.length - 1 : slidePosition - 1;
    } else {
      nextSlideIdx = slidePosition + 1 > photos.length - 1 ? 0 : slidePosition + 1;
    }
    setSlidePosition(nextSlideIdx);
  };

  if (idx < 0 || !photos.length || !photos[slidePosition]) {
    onClose();
    return null;
  }

  return (
    <Modal
      size={{ base: 'fullScreen', md: 'md' }}
      open={true}
      header={<Heading level={1}>Edit photo</Heading>}
      onOpenChange={(open) => !open && onClose()}
      body={
        <Box css={{ width: '100%', overflow: 'hidden', userSelect: 'none' }}>
          <Box css={{ flex: '1 0 100%' }} key={`${photos[slidePosition].id}-carousel-slot`}>
            <Box
              key={`photo-${photos[slidePosition].id}`}
              css={{ display: 'flex', flexDirection: { base: 'column', lg: 'row' } }}
            >
              <Box css={{ flexBasis: '50%' }}>
                <Box
                  css={{
                    alignItems: 'center',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'contain',
                    backgroundColor: 'bg.accent.gray.hero',
                    borderRadius: '100',
                    display: 'flex',
                    height: '362px',
                    justifyContent: 'space-between',
                    padding: '200',
                    width: '100%',
                  }}
                  style={{ backgroundImage: `url(${getUrlSrc(photos[slidePosition], getPhotoUrls, isRunningInProd)})` }}
                >
                  {photos.length > 1 && (
                    <>
                      <IconButton
                        icon={<IconChevronLeftCircleFilled />}
                        size="md"
                        emphasis="bare"
                        shape="circle"
                        css={{
                          color: 'text.onHero.action.neutral.default',
                          opacity: '85%',
                          _hover: { opacity: '100%' },
                        }}
                        onClick={() => getNextSlideIdx(LEFT)}
                        title="Previous"
                      />
                      <IconButton
                        icon={<IconChevronRightCircleFilled />}
                        size="md"
                        emphasis="bare"
                        shape="circle"
                        css={{
                          color: 'text.onHero.action.neutral.default',
                          opacity: '85%',
                          _hover: { opacity: '100%' },
                        }}
                        onClick={() => getNextSlideIdx(RIGHT)}
                        title="Next"
                      />
                    </>
                  )}
                </Box>
              </Box>

              <Box
                css={{
                  paddingLeft: { base: '0', lg: '400' },
                  marginRight: { base: '0', lg: '200' },
                  paddingTop: { base: '400', lg: '0' },
                  flexBasis: '50%',
                  flexDirection: 'column',
                }}
              >
                {isCaptionDisabled === false && (
                  <div data-testid="Caption">
                    <Text textStyle="body-bold" css={{ marginBottom: '200', display: 'block' }}>
                      Caption
                    </Text>

                    <TextareaHelper
                      isDisabled={isCaptionDisabled || isActionButtonDisabled}
                      initialValue={photos[slidePosition].caption}
                      onClearError={() => onClearError({ photoAtSlidePosition: photos[slidePosition], slidePosition })}
                      handleCaptionBlur={(caption: string) => {
                        setIsCaptionSaving(true);
                        if (onCaptionUpdate) {
                          onCaptionUpdate({
                            photoId: photos[slidePosition].id,
                            caption,
                            onComplete: () => {
                              setIsCaptionSaving(false);
                            },
                            slidePosition: slidePosition,
                          });
                        }
                      }}
                    />

                    {photos[slidePosition].error && <FormHelp error={true}>{photos[slidePosition].error}</FormHelp>}
                  </div>
                )}

                <Box css={{ display: 'flex', marginTop: '600' }}>
                  <Box css={{ display: 'flex', marginRight: '600', flexBasis: '45%' }}>
                    <TextButton
                      aria-label="Delete photo"
                      data-testid="PhotoDetailsModal-DeletePhotoButton"
                      tone="subtle"
                      disabled={isActionButtonDisabled}
                      icon={<IconDeleteOutline />}
                      onClick={() => {
                        setIsPhotoDeleting(true);
                        onDeletePhoto({
                          photoIdAtSlidePosition: photos[slidePosition].id,
                          onComplete: () => {
                            setIsPhotoDeleting(false);
                            if (photos.length === 1) {
                              setSlidePosition(0);
                            }
                          },
                          slidePosition,
                        });
                      }}
                    >
                      {isPhotoDeleting ? 'Deleting' : 'Delete'} photo
                    </TextButton>
                  </Box>
                </Box>

                {Boolean(
                  photos[slidePosition]?.isAppropriate === false || photos[slidePosition]?.errorDto?.errorCode,
                ) && (
                  <>
                    <Paragraph css={{ marginTop: '600', color: 'text.feedback.critical.hero' }}>
                      {photos[slidePosition]?.errorDto?.errorCode && photos[slidePosition]?.errorDto?.errorMessage
                        ? photos[slidePosition]?.errorDto?.errorMessage
                        : 'This photo is inappropriate or irrelevant for rental listings and will not appear on Zillow Rental Network.'}
                    </Paragraph>
                    <Paragraph css={{ marginTop: '400' }}>
                      To understand why this image has been flagged, refer to this{' '}
                      <Anchor
                        target="_blank"
                        href="https://help.zillowrentalmanager.com/hc/en-us/articles/18048174249363-Photos-Flagged-on-Zillow-Rental-Manager-Listings"
                      >
                        help article
                      </Anchor>
                      .
                    </Paragraph>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      }
    />
  );
};
